import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthToken, setAuthUser } from '../../redux/reducers/authSlice';
import { authApiCall } from '../../services/http';
import { endpoints } from '../../config';
import { SUB_STATUS_OPTIONS } from '../../helpers/subscriptions';

// Convierte un input controlado (string) al modelo (number | null).
const inputToNumber = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

// 'YYYY-MM-DD' (del input nativo type="date", en hora local) → ISO timestamptz a mediodía UTC.
const ymdToIso = (ymd) => {
  if (!ymd) return null;
  return new Date(`${ymd}T12:00:00.000Z`).toISOString();
};

const BILLING_INTERVAL_OPTIONS = [
  { value: '', label: '— sin definir —' },
  { value: 'month', label: 'Mensual' },
  { value: 'year', label: 'Anual' },
  { value: 'one_time', label: 'Pago único' },
];

CreateSubscriptionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func, // (createdSub) => void
};

export default function CreateSubscriptionDialog({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [subSource, setSubSource] = useState('manual'); // 'manual' | 'stripe'
  const [subType, setSubType] = useState('individual'); // 'individual' | 'team'
  const [userId, setUserId] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [maxSeats, setMaxSeats] = useState('');
  const [status, setStatus] = useState('active');
  const [billingInterval, setBillingInterval] = useState('');
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState('');
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState('');
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [comments, setComments] = useState('');
  const [pricePerUserEur, setPricePerUserEur] = useState('');
  const [flatPriceEur, setFlatPriceEur] = useState('');
  const [priceMode, setPriceMode] = useState('perUser'); // 'perUser' | 'flat'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const apiProps = { navigate, dispatch, setAuthUser, setAuthToken, authToken, redirectRoute: null };

  // Reset al abrir.
  useEffect(() => {
    if (!open) return;
    setSubSource('manual');
    setSubType('individual');
    setUserId('');
    setWorkspaceId('');
    setMaxSeats('');
    setStatus('active');
    setBillingInterval('');
    setCurrentPeriodEnd('');
    setStripeSubscriptionId('');
    setStripeCustomerId('');
    setComments('');
    setPricePerUserEur('');
    setFlatPriceEur('');
    setPriceMode('flat');
    setError(null);
  }, [open]);

  // Toggle de precio: por usuario XOR cerrado; al cambiar limpiamos el del otro modo.
  const handlePriceMode = (v) => {
    if (!v) return;
    if (v === 'flat') setPricePerUserEur('');
    else setFlatPriceEur('');
    setPriceMode(v);
  };

  const save = async () => {
    // Según el tipo, exigimos el id correspondiente (individual → userId, equipo → workspaceId).
    const uid = inputToNumber(userId);
    const wid = inputToNumber(workspaceId);
    if (subType === 'individual' && uid == null) {
      setError('Una sub individual necesita un userId.');
      return;
    }
    if (subType === 'team' && wid == null) {
      setError('Una sub de equipo necesita un workspaceId.');
      return;
    }
    if (subSource === 'stripe' && !stripeSubscriptionId.trim()) {
      setError('Una sub de Stripe necesita el stripeSubscriptionId.');
      return;
    }

    setSaving(true);
    setError(null);

    // Solo mandamos los campos que el admin ha rellenado; el resto lo deja el backend a su default/null.
    const body = {};
    // Individual → solo userId; equipo → solo workspaceId (nunca ambos).
    if (subType === 'individual') body.userId = uid;
    else body.workspaceId = wid;
    const ms = inputToNumber(maxSeats);
    if (ms != null) body.maxSeats = ms;
    if (status) body.status = status;
    if (comments.trim()) body.comments = comments.trim();

    if (subSource === 'stripe') {
      // Sub de Stripe: ids de Stripe. lastSyncAt se setea automático a ahora (fecha de creación).
      // El precio / facturación / periodo los deriva Stripe, no se mandan aquí.
      body.stripeSubscriptionId = stripeSubscriptionId.trim();
      if (stripeCustomerId.trim()) body.stripeCustomerId = stripeCustomerId.trim();
      body.lastSyncAt = new Date().toISOString();
    } else {
      // Sub manual: precio (por usuario XOR cerrado), facturación y fin de periodo.
      const ppu = inputToNumber(pricePerUserEur);
      const flat = inputToNumber(flatPriceEur);
      if (priceMode === 'perUser' && ppu != null) body.pricePerUserEur = ppu;
      if (priceMode === 'flat' && flat != null) body.flatPriceEur = flat;
      if (billingInterval) body.billingInterval = billingInterval;
      if (currentPeriodEnd) body.currentPeriodEnd = ymdToIso(currentPeriodEnd);
    }

    try {
      const res = await authApiCall({
        method: 'POST',
        endpoint: endpoints.subscriptions,
        path: '/',
        data: body,
        ...apiProps,
      });
      // Merge body + respuesta: el padre abre el drill-in con esto, así tiene tanto los
      // campos del formulario como el id/campos reales que devuelva el backend.
      if (onCreated) onCreated({ ...body, ...(res?.data || {}) });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Error al crear la suscripción');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>Crear suscripción</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Typography variant="caption" color="text.secondary">
            Crea una suscripción manual o vinculada a Stripe. Los seats (usuarios y sus planes) se gestionan después
            desde el drill-in de la suscripción.
          </Typography>

          <Stack spacing={1}>
            <Typography variant="subtitle2">Origen</Typography>
            <ToggleButtonGroup
              exclusive
              color="primary"
              size="small"
              value={subSource}
              onChange={(e, v) => {
                if (!v) return;
                setSubSource(v);
                setError(null);
              }}
            >
              <ToggleButton value="manual">Manual</ToggleButton>
              <ToggleButton value="stripe">Stripe</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack spacing={1}>
            <ToggleButtonGroup
              exclusive
              color="primary"
              size="small"
              value={subType}
              onChange={(e, v) => {
                if (!v) return; // no permitir deseleccionar
                setSubType(v);
                setError(null);
                // Limpiamos el id del otro tipo para no arrastrar un valor oculto.
                if (v === 'individual') setWorkspaceId('');
                else setUserId('');
              }}
            >
              <ToggleButton value="individual">Individual</ToggleButton>
              <ToggleButton value="team">Equipo</ToggleButton>
            </ToggleButtonGroup>

            {subType === 'individual' ? (
              <TextField
                label="userId (titular)"
                type="number"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                fullWidth
                helperText="ID del usuario titular de la sub individual"
                inputProps={{ min: 0, step: 1 }}
              />
            ) : (
              <TextField
                label="workspaceId"
                type="number"
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
                fullWidth
                helperText="ID del workspace al que pertenece la sub de equipo"
                inputProps={{ min: 0, step: 1 }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField select label="Estado" value={status} onChange={(e) => setStatus(e.target.value)} fullWidth>
              {SUB_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Asientos (maxSeats)"
              type="number"
              value={maxSeats}
              onChange={(e) => setMaxSeats(e.target.value)}
              fullWidth
              helperText="Vacío = sin límite"
              inputProps={{ min: 0, step: 1 }}
            />
          </Stack>

          <Divider />

          {subSource === 'manual' ? (
            <>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Precio</Typography>
                <ToggleButtonGroup
                  exclusive
                  color="primary"
                  size="small"
                  value={priceMode}
                  onChange={(e, v) => handlePriceMode(v)}
                >
                  <ToggleButton value="flat">Cerrado</ToggleButton>
                  <ToggleButton value="perUser">Por usuario</ToggleButton>
                </ToggleButtonGroup>
                {priceMode === 'flat' ? (
                  <TextField
                    label="Precio cerrado (EUR)"
                    type="number"
                    value={flatPriceEur}
                    onChange={(e) => setFlatPriceEur(e.target.value)}
                    fullWidth
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                ) : (
                  <TextField
                    label="Precio por usuario (EUR)"
                    type="number"
                    value={pricePerUserEur}
                    onChange={(e) => setPricePerUserEur(e.target.value)}
                    fullWidth
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                )}
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  select
                  label="Facturación"
                  value={billingInterval}
                  onChange={(e) => setBillingInterval(e.target.value)}
                  fullWidth
                >
                  {BILLING_INTERVAL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'none'} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Fin de periodo"
                  type="date"
                  value={currentPeriodEnd}
                  onChange={(e) => setCurrentPeriodEnd(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </>
          ) : (
            <>
              <TextField
                label="stripeSubscriptionId"
                value={stripeSubscriptionId}
                onChange={(e) => setStripeSubscriptionId(e.target.value)}
                fullWidth
                placeholder="sub_..."
              />
              <TextField
                label="stripeCustomerId"
                value={stripeCustomerId}
                onChange={(e) => setStripeCustomerId(e.target.value)}
                fullWidth
                placeholder="cus_..."
              />
              <Typography variant="caption" color="text.secondary">
                La última sincronización (lastSyncAt) se setea automáticamente a la fecha de creación. El precio,
                facturación y periodo los deriva Stripe.
              </Typography>
            </>
          )}

          <TextField
            label="Comentarios"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <LoadingButton onClick={save} variant="contained" loading={saving}>
          Crear
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
